import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import {cva, type VariantProps} from "class-variance-authority"

import {cn} from "@/shared/utils/utils.ts"

// Apple-inspired Label with variants and accessibility helpers
const labelVariants = cva(
  "font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  {
    variants: {
      size: {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base",
      },
      // Visual invalid state (inputs should own aria-invalid)
      invalid: {
        true: "text-destructive",
        false: "",
      },
      // Show asterisk after label text (visual only)
      required: {
        true: "after:ml-0.5 after:content-['*'] after:text-destructive",
        false: "",
      },
      // Visually hide while keeping accessible to screen readers
      visuallyHidden: {
        true: "sr-only",
        false: "",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

type LabelProps = React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
  VariantProps<typeof labelVariants>

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, size, invalid, required, visuallyHidden, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants({ size, invalid, required, visuallyHidden }), className)}
    {...props}
  />
))
Label.displayName = LabelPrimitive.Root.displayName

export { Label }
